<template>
  <div class="doc-editor-layout">
    <!-- 左侧加载状态（默认显示） -->
    <DocumentLoadingPanel
      v-if="!editorReady"
      :keyword="keyword"
      @finish="handleLoadFinish"
    />

    <!-- 左侧编辑器（点击生成Word后显示） -->
    <EditorPanel
      v-else
      ref="editorRef"
      class="layout-left"
      v-model="content"
      :title="title"
      :downloadName="downloadName"
      @change="onEditorChange"
    />

    <!-- 右侧AI面板，点击应用触发handleApply -->
    <AiAssistantPanel
      class="layout-right"
      :initialQuery="initialQuery"
      :initialStructured="initialStructured"
      @apply="handleApply"
      @download-word="handleDownloadWord"
      @quota-exceeded="(msg) => emit('quota-exceeded', msg)"
    />
  </div>
</template>

<script setup>
import { ref, nextTick, watch } from "vue";
import { message } from "ant-design-vue";
import EditorPanel from "./left/EditorPanel.vue";
import AiAssistantPanel from "./right/AiAssistantPanel.vue";
import DocumentLoadingPanel from "@/components/documenthome/DocumentLoadingPanel.vue";
import { SaveIndictmentConversation } from "../../api/case/case";

defineProps({
  initialQuery: {
    type: String,
    default: "",
  },
  initialStructured: {
    type: Object,
    default: null,
  },
  keyword: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["change", "quota-exceeded"]);

const title = ref("民事起诉状");
const content = ref("");
// 绑定编辑器ref
const editorRef = ref(null);
const editorReady = ref(false);

// 编辑器就绪状态（文档已生成/加载）通知父级，用于控制保存/下载按钮可用性
watch(editorReady, (val) => emit("editor-ready", val));

// 自动保存相关
const lastSaveTime = ref("");
let autoSaveTimer = null;
const AUTO_SAVE_INTERVAL = 120000; // 2 分钟

/**
 * 编辑器内容变更
 */
const onEditorChange = () => {
  emit("change");
  // 每次内容变更，重置自动保存定时器
  resetAutoSaveTimer();
};

/**
 * 重置自动保存定时器
 */
const resetAutoSaveTimer = () => {
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
    autoSaveTimer = null;
  }
  autoSaveTimer = setTimeout(() => {
    doAutoSave();
  }, AUTO_SAVE_INTERVAL);
};

/**
 * 执行自动保存（仅 API 上传，不触发编辑器本地下载）
 */
const doAutoSave = async () => {
  if (!editorRef.value) return;
  try {
    const now = new Date();
    const h = String(now.getHours()).padStart(2, "0");
    const m = String(now.getMinutes()).padStart(2, "0");
    const s = String(now.getSeconds()).padStart(2, "0");
    lastSaveTime.value = `${h}:${m}:${s}`;

    // 2. API 上传保存
    uploadToApi();
  } catch (err) {
    console.error("自动保存失败：", err);
  }
};

/**
 * 获取当前文档 Blob 并通过 API 上传保存
 */
const uploadToApi = async () => {
  const sessionId = window.__docSessionId;
  if (!sessionId) return;

  // 更新保存时间
  const now = new Date();
  const h = String(now.getHours()).padStart(2, "0");
  const m = String(now.getMinutes()).padStart(2, "0");
  const s = String(now.getSeconds()).padStart(2, "0");
  lastSaveTime.value = `${h}:${m}:${s}`;

  try {
    const blob = await editorRef.value.getBlob();
    if (!blob) return;
    const file = new File([blob], `document_${Date.now()}.docx`, {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
    await SaveIndictmentConversation(sessionId, file);
    message.success("文档生成成功");
  } catch (err) {
    console.error("API 保存失败：", err);
    message.error("文档生成失败");
  }
};

/**
 * 加载面板完成（预加载完成时可切换编辑器）
 */
const handleLoadFinish = () => {
  // 加载面板动画结束后，如果文档已经就绪则切换，否则保持等待用户点击生成Word
};

/**
 * AI面板点击【生成Word并打开】
 * 切换左侧为编辑器并加载文档
 */
const downloadName = ref("");
const handleDownloadWord = async (url, name) => {
  editorReady.value = true;
  downloadName.value = name;
  await nextTick();
  if (!editorRef.value || !url) return;
  await editorRef.value.loadDocument(url);
};

/**
 * AI面板点击【应用】触发
 * @param {string} text AI生成的文书文本
 */
const handleApply = async (text) => {
  if (!editorRef.value || !text) return;
  await editorRef.value.insertText(text);
};

/**
 * 保存当前文档
 */
const save = async (fileName) => {
  if (!editorRef.value) return;
  await editorRef.value.save(fileName);
};

/**
 * 加载远程文档 URL
 */
const loadDocument = async (url, name) => {
  editorReady.value = true;
  if (name) downloadName.value = name;
  await nextTick();
  if (!editorRef.value || !url) return;
  await editorRef.value.loadDocument(url);
};

defineExpose({
  save,
  uploadToApi,
  loadDocument,
  lastSaveTime,
});
</script>

<style scoped lang="less">
.doc-editor-layout {
  height: calc(100vh - 112px);
  display: flex;
  gap: 16px;
  // padding: 16px;
  background: #f6f8fc;
  box-sizing: border-box;
}

.layout-left {
  flex: 1;
  min-width: 0;
}

.layout-right {
  width: 580px;
  flex-shrink: 0;
}

/* 加载面板占满左侧空间 */
:deep(.loading-container) {
  flex: 1;
}
</style>
